import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useFocusTrap } from '../hooks/useFocusTrap'
import {
  CHECK_UPDATE_EVENT,
  checkForUpdate,
  installUpdate,
  updatesSupported,
  type UpdateCheck,
} from '../lib/appUpdate'
import { CheckIcon, DownloadIcon, WarningIcon } from './icons'

/** "Sonra" denince ayni surum 24 saat boyunca acilista yeniden sorulmuyor. */
const SNOOZE_KEY = 'hedefai.updateSnooze'
const SNOOZE_MS = 24 * 60 * 60 * 1000

type View =
  | { kind: 'available'; check: UpdateCheck }
  | { kind: 'downloading'; check: UpdateCheck; percent: number }
  | { kind: 'installer' }
  | { kind: 'permission'; check: UpdateCheck }
  | { kind: 'uptodate'; current: string }
  | { kind: 'error' }

function snoozed(version: string): boolean {
  try {
    const raw = window.localStorage.getItem(SNOOZE_KEY)
    if (!raw) return false
    const value = JSON.parse(raw) as { version?: string; until?: number }
    return value.version === version && (value.until ?? 0) > Date.now()
  } catch {
    return false
  }
}

function snooze(version: string): void {
  try {
    window.localStorage.setItem(
      SNOOZE_KEY,
      JSON.stringify({ version, until: Date.now() + SNOOZE_MS }),
    )
  } catch {
    /* depolama kapali: ertelenmez, bir sonraki acilista yine sorulur */
  }
}

function megabytes(bytes: number): string {
  return `⁨${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB⁩`
}

/**
 * Android uygulamasinin guncelleme penceresi (bkz. `lib/appUpdate.ts`).
 *
 * - Acilista kendiliginden denetler; yeni surum varsa sorar ("Sonra" ayni surumu
 *   24 saat erteler).
 * - Profil menusundeki "Guncellemeleri denetle" (`CHECK_UPDATE_EVENT`) elle
 *   denetler; guncelse bunu da soyler.
 * Eklentisi olmayan ortamlarda (tarayici, masaustu, eski APK) hicbir sey yapmaz.
 */
export function AppUpdatePrompt() {
  const { t } = useTranslation()
  const [view, setView] = useState<View | null>(null)

  const run = useCallback(async (manual: boolean) => {
    try {
      const check = await checkForUpdate()
      if (!check) return
      if (check.available && check.url) {
        if (!manual && snoozed(check.latest ?? '')) return
        setView({ kind: 'available', check })
      } else if (manual) {
        setView({ kind: 'uptodate', current: check.current })
      }
    } catch {
      if (manual) setView({ kind: 'error' })
    }
  }, [])

  useEffect(() => {
    if (!updatesSupported()) return
    void run(false)
    const onManual = () => void run(true)
    window.addEventListener(CHECK_UPDATE_EVENT, onManual)
    return () => window.removeEventListener(CHECK_UPDATE_EVENT, onManual)
  }, [run])

  const startInstall = useCallback(async (check: UpdateCheck) => {
    if (!check.url) return
    setView({ kind: 'downloading', check, percent: 0 })
    try {
      const status = await installUpdate(check.url, (percent) =>
        setView({ kind: 'downloading', check, percent }),
      )
      setView(status === 'needs_permission' ? { kind: 'permission', check } : { kind: 'installer' })
    } catch {
      setView({ kind: 'error' })
    }
  }, [])

  if (!view) return null
  const close = () => setView(null)

  if (view.kind === 'available') {
    const { check } = view
    return (
      <UpdateDialog
        icon={<DownloadIcon size={20} />}
        title={t('appUpdate.available.title')}
        message={t('appUpdate.available.body', {
          latest: check.latest,
          current: check.current,
          size: megabytes(check.sizeBytes),
        })}
        secondary={{
          label: t('appUpdate.later'),
          onClick: () => {
            snooze(check.latest ?? '')
            close()
          },
        }}
        primary={{ label: t('appUpdate.update'), onClick: () => void startInstall(check) }}
        onClose={close}
      />
    )
  }
  if (view.kind === 'downloading') {
    return (
      <UpdateDialog
        icon={<DownloadIcon size={20} />}
        title={t('appUpdate.downloading.title')}
        message={t('appUpdate.downloading.body', { percent: view.percent })}
        progress={view.percent}
        onClose={() => undefined}
      />
    )
  }
  if (view.kind === 'installer') {
    return (
      <UpdateDialog
        icon={<CheckIcon size={20} />}
        title={t('appUpdate.installer.title')}
        message={t('appUpdate.installer.body')}
        primary={{ label: t('appUpdate.ok'), onClick: close }}
        onClose={close}
      />
    )
  }
  if (view.kind === 'permission') {
    const { check } = view
    return (
      <UpdateDialog
        icon={<WarningIcon size={20} />}
        title={t('appUpdate.permission.title')}
        message={t('appUpdate.permission.body')}
        secondary={{ label: t('appUpdate.close'), onClick: close }}
        primary={{ label: t('appUpdate.retry'), onClick: () => void startInstall(check) }}
        onClose={close}
      />
    )
  }
  if (view.kind === 'uptodate') {
    return (
      <UpdateDialog
        icon={<CheckIcon size={20} />}
        title={t('appUpdate.uptodate.title')}
        message={t('appUpdate.uptodate.body', { current: view.current })}
        primary={{ label: t('appUpdate.ok'), onClick: close }}
        onClose={close}
      />
    )
  }
  return (
    <UpdateDialog
      icon={<WarningIcon size={20} />}
      title={t('appUpdate.error.title')}
      message={t('appUpdate.error.body')}
      primary={{ label: t('appUpdate.ok'), onClick: close }}
      onClose={close}
    />
  )
}

interface Action {
  label: string
  onClick(): void
}

function UpdateDialog({
  icon,
  title,
  message,
  primary,
  secondary,
  progress,
  onClose,
}: {
  icon: React.ReactNode
  title: string
  message: string
  primary?: Action
  secondary?: Action
  progress?: number
  onClose(): void
}) {
  const container = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const messageId = useId()
  useFocusTrap(container, { active: true, onClose })

  return createPortal(
    <div className="confirm-backdrop">
      <div
        ref={container}
        className="confirm-dialog confirm-info"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        tabIndex={-1}
      >
        <div className="confirm-body">
          <span className="confirm-icon" aria-hidden="true">
            {icon}
          </span>
          <div className="confirm-text">
            <h2 id={titleId}>{title}</h2>
            <p id={messageId}>{message}</p>
            {progress !== undefined && (
              <div
                className="update-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
              >
                <span style={{ inlineSize: `${progress}%` }} />
              </div>
            )}
          </div>
        </div>
        {(primary || secondary) && (
          <div className="confirm-actions">
            {secondary && (
              <button type="button" className="confirm-btn" onClick={secondary.onClick}>
                {secondary.label}
              </button>
            )}
            {primary && (
              <button
                type="button"
                className="confirm-btn confirm-btn-primary"
                onClick={primary.onClick}
              >
                {primary.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
